import "jspdf-autotable";
import "primeicons/primeicons.css";
import { Calendar } from "primereact/calendar";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import { useEffect, useState } from "react";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import ExcelDownloadButton from "../../component/DownloadButton/ExcelDownloadButton";
import PdfDownloadButton from "../../component/DownloadButton/PdfDownloadButton";
import Pagination from "../../component/Pagination";
import IconSearch from "../../component/SvgIcons/IconSearch";
import IconUpdownFilter from "../../component/SvgIcons/IconUpdownFilter";
import IconCalendarRegistration from "../../component/SvgIcons/IconCalendarRegistration";
import api from "../../Services/api";
import '../EventRegistration/index.css';
import { formatCountryDate } from "../../Services/countryTime";
Modal.setAppElement("#root");

export default function EventRegistration() {
  const [searchQuery, setSearchQuery] = useState("");
  const [customerData, setCustomerData] = useState([]);
  const [pastEvent, setPastEvent] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [, setSortConfig] = useState({ key: null, direction: "asc" });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [dates, setDates] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1920) {
        setItemsPerPage(20);
      } else {
        setItemsPerPage(10);
      }
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    // Fetch data from the API when the component mounts
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.post("/GetEventCustomers");          
        console.log("API Response:", response.data.data);
         setPastEvent(response.data.data);     
        // Sort data in descending order by default (latest event first)
        const today = new Date();
        today.setHours(0, 0, 0, 0); // reset to start of today

          // Keep only upcoming events
          const upcomingEvents = response.data.data.filter(
            (event) => new Date(event.event_date) >= today
          );

          // Sort by date (latest upcoming first or soonest first – your choice)
          const sortedData = upcomingEvents.sort(
            //(a, b) => new Date(a.event_date) - new Date(b.event_date) // soonest first
             (a, b) => new Date(b.event_date) - new Date(a.event_date)
          );

        setCustomerData(sortedData);
        setSortConfig({ key: "event_date", direction: "desc" });
      } catch (error) {
        console.error("Error fetching events:", error);
      }finally {
      setLoading(false); // stop loading
    }
    };

    fetchData();
  }, []); // Empty dependency array means this effect runs once when the component mounts

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to the first page on search
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const newDirection = prev.key === key && prev.direction === "asc" ? "desc" : "asc";

      const sortedData = [...customerData].sort((a, b) => {
        if (key.includes("date")) {
          return newDirection === "asc"
            ? new Date(a[key]) - new Date(b[key])
            : new Date(b[key]) - new Date(a[key]);
        } else {
          return newDirection === "asc"
            ? a[key].toString().localeCompare(b[key].toString())
            : b[key].toString().localeCompare(a[key].toString());
        }
      });


      setCustomerData(sortedData);

      return { key, direction: newDirection };
    });
  };

  const filteredEventData = customerData.filter((customer) => {
    const searchLower = searchQuery.toLowerCase().trim();
    const formattedSearchDate = normalizeDateFormat(searchQuery);

    const eventDate = new Date(customer.event_date);
    eventDate.setHours(0, 0, 0, 0); // Remove time for accurate comparison

    const startDate = dates?.[0] ? new Date(dates[0]) : null;
    const endDate = dates?.[1] ? new Date(dates[1]) : null;

    if (startDate && endDate) {
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999); // Ensure the entire end date is included
    }

    const isWithinDateRange =
      startDate && endDate ? eventDate >= startDate && eventDate <= endDate : true; // Show all if no date selected

    return (
      isWithinDateRange && (
        customer.name?.toLowerCase().includes(searchLower) ||
        customer.email?.toLowerCase().includes(searchLower) ||
        customer.organization?.toLowerCase().includes(searchLower) ||
        customer.confirmation?.toLowerCase().includes(searchLower) ||
        customer.mobile?.includes(searchQuery) ||
        customer.tallynumber?.includes(searchQuery) ||
        customer.NoOfParticipants?.includes(searchQuery) ||
        normalizeDateFormat(customer.registration_date) === formattedSearchDate ||
        normalizeDateFormat(customer.event_date) === formattedSearchDate ||
        customer.Title?.toLowerCase().includes(searchLower) ||
        customer.ModeOfMeeting?.toLowerCase().includes(searchLower)
    )   
    );
  });

  // Function to normalize date formats (converts everything to "DD-MM-YYYY")
  function normalizeDateFormat(date) {
    if (!date) return "";

    // If date is already in "D-MM-YYYY" or "DD-MM-YYYY" format, normalize it
    if (/^\d{1,2}-\d{2}-\d{4}$/.test(date)) {
      const [day, month, year] = date.split("-");
      return `${day.padStart(2, "0")}-${month}-${year}`; // Ensures "DD-MM-YYYY" format
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate)) return ""; // Return empty if invalid date

    // Extract day, month, and year
    return formatCountryDate(parsedDate).replace(/\//g, "-"); // Format as "DD-MM-YYYY"
  }
  // Calculate indices for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEventData.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  // Pagination handling
  const totalPages = Math.ceil(filteredEventData.length / itemsPerPage);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const formatTime = (time) => {
    if (!time) return ""; // Return empty string if time is null/undefined

    // Ensure time has seconds for correct parsing
    const normalizedTime = time.length === 5 ? `${time}:00` : time;

    // Use local time instead of UTC to avoid timezone issues
    const [hour, minute, second] = normalizedTime.split(":").map(Number);
    const date = new Date();
    date.setHours(hour, minute, second || 0);

    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

 // Convert event_date into JS Date objects from API data
const dateTemplate = (dateMeta) => {
  const { day, month, year } = dateMeta;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Find the matching event for this date
  const matchingEvent = pastEvent.find((event) => {
    const d = new Date(event.event_date);
    d.setHours(0, 0, 0, 0);
    return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
  });

  // Determine class based on past/future
  let className = "";
  if (matchingEvent) {
    const eventDate = new Date(matchingEvent.event_date);
    eventDate.setHours(0, 0, 0, 0);

    if (eventDate < today) {
      className = "btn-danger past-event-date"; // past = red
    } else if (eventDate > today) {
      className = "btn-success future-event-date"; // future = green
    } else {
      className = "btn-primary today-event-date"; // today = purple or any style
    }
  }

  return (
    <span className={className}>
      {day}
    </span>
  );
};
  return (
    <section className="eventpage">
    <div className="d-flex justify-content-center  headercustomerpage">
     Event-wise Registration Analysis Report 
    </div>

      {/* <div className="pageLeftspace"> */}
      <div className="eventregistration">
        <div className="d-flex justify-content-between align-items-center topsearchbar minfilter">
          <div className="d-flex flex-column flex-md-row gap-2 calenderfilter">
            <div className="d-flex gap-2 justify-content-center">
               <span className="btn btn-purpal badge bg-success fw-normal">
              Total Participants:{" "}
              {filteredEventData.reduce(
                (total, item) =>
                  total + (parseInt(item.NoOfParticipants) || 0),
                0
              )}
            </span>            
              <span className="btn btn-purpal badge bg-primary fw-normal">
                {filteredEventData.length} Records Found
              </span>
              </div>
            
            <Calendar
              value={dates}           
            onChange={(e) => {
                setDates(e.value);

                if (e.value && Array.isArray(e.value)) {
                const [startDate, endDate] = e.value;

                if (startDate && endDate) {
                    // 📌 Range selected
                    const sDate = new Date(startDate);
                      const eDate = new Date(endDate);
                      sDate.setHours(0,0,0,0);
                      eDate.setHours(23,59,59,999);
                      const filtered = pastEvent.filter((event) => {
                          const eventDate = new Date(event.event_date);
                          eventDate.setHours(0,0,0,0);
                          return eventDate >= sDate && eventDate <= eDate;
                      });
                      setCustomerData(filtered);
                } else if (startDate && !endDate) {
                    // 📌 Only one date selected → filter for exact date
                    const targetDate = new Date(startDate);
                    targetDate.setHours(0, 0, 0, 0);

                    const filtered = pastEvent.filter((event) => {
                    const eventDate = new Date(event.event_date);
                    eventDate.setHours(0, 0, 0, 0);
                    return eventDate.getTime() === targetDate.getTime();
                    });

                    setCustomerData(filtered);
                } else {
                    // 📌 Reset (no dates)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const upcomingEvents = pastEvent.filter(
                    (event) => new Date(event.event_date) >= today
                    );
                    setCustomerData(upcomingEvents);
                }

                setCurrentPage(1);
                }
            }}
              selectionMode="range"
              readOnlyInput
              className="Calendar"
              panelClassName="custom-calendar-panel" 
              placeholder="Select Date Range"
              dateFormat="dd-mm-yy"
              showIcon
              icon={<IconCalendarRegistration />}
              dateTemplate={dateTemplate}
            />
          <div className="d-flex gap-2 justify-content-center">
           {localStorage.getItem("AdminUser_Type") === "Admin" && (
              <Link
                to="/event-wise-user-registration-report"
                type="button"
                className="btn btn-purpal addbutton"
              >
                User Wise Report
              </Link>
            )}

              <Link to="#" className="d-flex align-items-center" onClick={() => handleSort("event_date")}>
                <IconUpdownFilter />
              </Link>
          </div>
          </div>

           <div className="d-flex gap-2 flex-column flex-md-row justify-content-end searchright">     
          <div className="input-container">
            <input
              type="text"
              id="name"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search Here.."
            />

            {/* <label htmlFor="name">Search</label> */}
            <span className="underline"></span>
            <div className="search-icon">
              <IconSearch />
            </div>
          </div>
            <div className="d-flex gap-2 justify-content-center">
           <ExcelDownloadButton data={filteredEventData} />
           <PdfDownloadButton data={filteredEventData} />   
           </div>
           </div>

        </div>

        <div className="tableheader d-flex flex-column gap-4">
          <div className="event-detailstable">
            <table className="table">
              <thead>
                <tr>
                  <th width="10" align="center">
                    Sr. No.
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile no.</th>
                  <th>Organization Name</th>
                  <th>Tally Number</th>
                  <th>Confirmation by</th>
                  <th>Participants</th>
                  <th className="text-start">Event Date</th>
                  <th className="text-start">Event Time </th>
                  <th>Evemt Title</th>
                  <th className="text-start">Register Date</th>
                  <th align="center">Mode of Meeting</th>
                </tr>
              </thead>
             <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13">                    
                    <div>Loading events...</div>
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((customer, index) => (
                  <tr key={customer.id}>
                    <td align="center">{indexOfFirstItem + index + 1}</td>
                    <td>{customer.name}</td>
                    <td>{customer.email}</td>
                    <td>{customer.mobile}</td>
                    <td>{customer.organization}</td>
                    <td>{customer.tallynumber}</td>
                    <td>{customer.confirmation}</td>
                    <td align="center">{customer.NoOfParticipants}</td>
                    <td>
                      {formatCountryDate(customer.event_date).replace(/\//g, "-")}
                    </td>
                    <td>{formatTime(customer.StartTime)} - {formatTime(customer.EndTime)}</td>
                    <td>{customer.Title}</td>
                    <td>
                      {formatCountryDate(customer.registration_date).replace(/\//g, "-")}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {customer.ModeOfMeeting && (
                        <span
                          className={`status-badge ${
                            customer.ModeOfMeeting?.toLowerCase() === "online meeting"
                              ? "status-active"
                              : "status-deactive"
                          }`}
                        >
                          {customer.ModeOfMeeting}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="13" style={{ textAlign: "center", padding: "10px" }}>
                    No data found
                  </td>
                </tr>
              )}
            </tbody>

            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={
              filteredEventData.length > itemsPerPage ? totalPages : 0
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </section>
  );
}
